/**
 * DateUtils - Comprehensive date and time formatting utilities
 * 
 * Features:
 * - Bulgarian and English date formatting
 * - Relative time formatting ("2 hours ago")
 * - Timezone handling for event times
 * - Date validation and parsing
 * - Duration calculation and formatting
 * - Calendar utilities for date selection
 */

/**
 * Date format constants
 */
export const DATE_FORMATS = {
  // Full date formats
  FULL_DATE_BG: 'DD.MM.YYYY',
  FULL_DATE_EN: 'MM/DD/YYYY',
  FULL_DATE_ISO: 'YYYY-MM-DD',
  
  // Date with time formats
  DATETIME_BG: 'DD.MM.YYYY HH:mm',
  DATETIME_EN: 'MM/DD/YYYY HH:mm',
  DATETIME_ISO: 'YYYY-MM-DDTHH:mm:ss',
  
  // Time formats
  TIME_24H: 'HH:mm',
  TIME_12H: 'hh:mm A',
  TIME_WITH_SECONDS: 'HH:mm:ss',
  
  // Short formats
  SHORT_DATE_BG: 'DD.MM',
  SHORT_DATE_EN: 'MM/DD',
  SHORT_DATETIME_BG: 'DD.MM HH:mm',
  SHORT_DATETIME_EN: 'MM/DD HH:mm',
  
  // Month formats
  MONTH_YEAR_BG: 'MMMM YYYY',
  MONTH_YEAR_EN: 'MMMM YYYY',
  SHORT_MONTH_BG: 'MMM YYYY',
  SHORT_MONTH_EN: 'MMM YYYY'
};

/**
 * Month names in different languages
 */
const MONTH_NAMES = {
  bg: {
    full: [
      'януари', 'февруари', 'март', 'април', 'май', 'юни',
      'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'
    ],
    short: ['яну', 'фев', 'мар', 'апр', 'май', 'юни', 'юли', 'авг', 'сеп', 'окт', 'ное', 'дек']
  },
  en: {
    full: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  }
};

/**
 * Day names in different languages
 */
const DAY_NAMES = {
  bg: {
    full: ['неделя', 'понеделник', 'вторник', 'сряда', 'четвъртък', 'петък', 'събота'],
    short: ['нед', 'пон', 'вто', 'сря', 'чет', 'пет', 'съб']
  },
  en: {
    full: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    short: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  }
};

/**
 * DateUtils class with comprehensive date/time utilities
 */
class DateUtils {
  /**
   * Parse a date string or object into a Date object
   * @param {string|Date|number} date - Date to parse
   * @returns {Date|null} Parsed Date object or null if invalid
   */
  static parseDate(date) {
    if (!date) return null;
    
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    
    if (typeof date === 'number') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    if (typeof date === 'string') {
      // Try ISO format first
      let parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
      
      // Try DD.MM.YYYY format (Bulgarian)
      if (date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const [day, month, year] = date.split('.');
        parsed = new Date(year, month - 1, day);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      
      // Try DD.MM.YYYY HH:mm format
      if (date.match(/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/)) {
        const [datePart, timePart] = date.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hours, minutes] = timePart.split(':');
        parsed = new Date(year, month - 1, day, hours, minutes);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
    }
    
    return null;
  }

  /**
   * Validate if a date is valid
   * @param {string|Date|number} date - Date to validate
   * @returns {boolean} True if valid date
   */
  static isValidDate(date) {
    return this.parseDate(date) !== null;
  }

  /**
   * Format date according to specified format and language
   * @param {string|Date} date - Date to format
   * @param {string} format - Format string (use DATE_FORMATS constants)
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {string} Formatted date string
   */
  static formatDate(date, format = DATE_FORMATS.FULL_DATE_BG, language = 'bg') {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return '';
    
    const year = parsedDate.getFullYear();
    const month = parsedDate.getMonth();
    const day = parsedDate.getDate();
    const hours = parsedDate.getHours();
    const minutes = parsedDate.getMinutes();
    const seconds = parsedDate.getSeconds();
    const dayOfWeek = parsedDate.getDay();
    
    let formatted = format;
    
    // Replace year
    formatted = formatted.replace('YYYY', year.toString());
    formatted = formatted.replace('YY', year.toString().slice(-2));
    
    // Replace month
    formatted = formatted.replace('MMMM', MONTH_NAMES[language].full[month]);
    formatted = formatted.replace('MMM', MONTH_NAMES[language].short[month]);
    formatted = formatted.replace('MM', String(month + 1).padStart(2, '0'));
    formatted = formatted.replace('M', (month + 1).toString());
    
    // Replace day
    formatted = formatted.replace('DDDD', DAY_NAMES[language].full[dayOfWeek]);
    formatted = formatted.replace('DDD', DAY_NAMES[language].short[dayOfWeek]);
    formatted = formatted.replace('DD', String(day).padStart(2, '0'));
    formatted = formatted.replace('D', day.toString());
    
    // Replace hours
    const hours12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    formatted = formatted.replace('HH', String(hours).padStart(2, '0'));
    formatted = formatted.replace('hh', String(hours12).padStart(2, '0'));
    formatted = formatted.replace('H', hours.toString());
    formatted = formatted.replace('h', hours12.toString());
    formatted = formatted.replace('A', ampm);
    formatted = formatted.replace('a', ampm.toLowerCase());
    
    // Replace minutes
    formatted = formatted.replace('mm', String(minutes).padStart(2, '0'));
    formatted = formatted.replace('m', minutes.toString());
    
    // Replace seconds
    formatted = formatted.replace('ss', String(seconds).padStart(2, '0'));
    formatted = formatted.replace('s', seconds.toString());
    
    return formatted;
  }

  /**
   * Format date for Bulgarian display (DD.MM.YYYY)
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date string
   */
  static formatDateBG(date) {
    return this.formatDate(date, DATE_FORMATS.FULL_DATE_BG, 'bg');
  }

  /**
   * Format date for English display (MM/DD/YYYY)
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date string
   */
  static formatDateEN(date) {
    return this.formatDate(date, DATE_FORMATS.FULL_DATE_EN, 'en');
  }

  /**
   * Format date with time (DD.MM.YYYY HH:mm)
   * @param {string|Date} date - Date to format
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {string} Formatted datetime string
   */
  static formatDateTime(date, language = 'bg') {
    const format = language === 'bg' ? DATE_FORMATS.DATETIME_BG : DATE_FORMATS.DATETIME_EN;
    return this.formatDate(date, format, language);
  }

  /**
   * Format time only (HH:mm)
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted time string
   */
  static formatTime(date) {
    return this.formatDate(date, DATE_FORMATS.TIME_24H);
  }

  /**
   * Get relative time string (e.g., "2 hours ago", "in 3 days")
   * @param {string|Date} date - Date to compare
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {string} Relative time string
   */
  static getRelativeTime(date, language = 'bg') {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return '';
    
    const now = new Date();
    const diffMs = now - parsedDate;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    const isFuture = diffMs < 0;
    const absDiffSeconds = Math.abs(diffSeconds);
    const absDiffMinutes = Math.abs(diffMinutes);
    const absDiffHours = Math.abs(diffHours);
    const absDiffDays = Math.abs(diffDays);
    const absDiffWeeks = Math.abs(diffWeeks);
    const absDiffMonths = Math.abs(diffMonths);
    const absDiffYears = Math.abs(diffYears);
    
    const translations = {
      bg: {
        justNow: 'точно сега',
        secondsAgo: (n) => `преди ${n} ${n === 1 ? 'секунда' : 'секунди'}`,
        minutesAgo: (n) => `преди ${n} ${n === 1 ? 'минута' : 'минути'}`,
        hoursAgo: (n) => `преди ${n} ${n === 1 ? 'час' : 'часа'}`,
        daysAgo: (n) => `преди ${n} ${n === 1 ? 'ден' : 'дни'}`,
        weeksAgo: (n) => `преди ${n} ${n === 1 ? 'седмица' : 'седмици'}`,
        monthsAgo: (n) => `преди ${n} ${n === 1 ? 'месец' : 'месеца'}`,
        yearsAgo: (n) => `преди ${n} ${n === 1 ? 'година' : 'години'}`,
        inSeconds: (n) => `след ${n} ${n === 1 ? 'секунда' : 'секунди'}`,
        inMinutes: (n) => `след ${n} ${n === 1 ? 'минута' : 'минути'}`,
        inHours: (n) => `след ${n} ${n === 1 ? 'час' : 'часа'}`,
        inDays: (n) => `след ${n} ${n === 1 ? 'ден' : 'дни'}`,
        inWeeks: (n) => `след ${n} ${n === 1 ? 'седмица' : 'седмици'}`,
        inMonths: (n) => `след ${n} ${n === 1 ? 'месец' : 'месеца'}`,
        inYears: (n) => `след ${n} ${n === 1 ? 'година' : 'години'}`
      },
      en: {
        justNow: 'just now',
        secondsAgo: (n) => `${n} ${n === 1 ? 'second' : 'seconds'} ago`,
        minutesAgo: (n) => `${n} ${n === 1 ? 'minute' : 'minutes'} ago`,
        hoursAgo: (n) => `${n} ${n === 1 ? 'hour' : 'hours'} ago`,
        daysAgo: (n) => `${n} ${n === 1 ? 'day' : 'days'} ago`,
        weeksAgo: (n) => `${n} ${n === 1 ? 'week' : 'weeks'} ago`,
        monthsAgo: (n) => `${n} ${n === 1 ? 'month' : 'months'} ago`,
        yearsAgo: (n) => `${n} ${n === 1 ? 'year' : 'years'} ago`,
        inSeconds: (n) => `in ${n} ${n === 1 ? 'second' : 'seconds'}`,
        inMinutes: (n) => `in ${n} ${n === 1 ? 'minute' : 'minutes'}`,
        inHours: (n) => `in ${n} ${n === 1 ? 'hour' : 'hours'}`,
        inDays: (n) => `in ${n} ${n === 1 ? 'day' : 'days'}`,
        inWeeks: (n) => `in ${n} ${n === 1 ? 'week' : 'weeks'}`,
        inMonths: (n) => `in ${n} ${n === 1 ? 'month' : 'months'}`,
        inYears: (n) => `in ${n} ${n === 1 ? 'year' : 'years'}`
      }
    };
    
    const t = translations[language];
    
    if (absDiffSeconds < 10) {
      return t.justNow;
    }
    
    if (isFuture) {
      if (absDiffYears > 0) return t.inYears(absDiffYears);
      if (absDiffMonths > 0) return t.inMonths(absDiffMonths);
      if (absDiffWeeks > 0) return t.inWeeks(absDiffWeeks);
      if (absDiffDays > 0) return t.inDays(absDiffDays);
      if (absDiffHours > 0) return t.inHours(absDiffHours);
      if (absDiffMinutes > 0) return t.inMinutes(absDiffMinutes);
      return t.inSeconds(absDiffSeconds);
    } else {
      if (absDiffYears > 0) return t.yearsAgo(absDiffYears);
      if (absDiffMonths > 0) return t.monthsAgo(absDiffMonths);
      if (absDiffWeeks > 0) return t.weeksAgo(absDiffWeeks);
      if (absDiffDays > 0) return t.daysAgo(absDiffDays);
      if (absDiffHours > 0) return t.hoursAgo(absDiffHours);
      if (absDiffMinutes > 0) return t.minutesAgo(absDiffMinutes);
      return t.secondsAgo(absDiffSeconds);
    }
  }

  /**
   * Calculate duration between two dates
   * @param {string|Date} startDate - Start date
   * @param {string|Date} endDate - End date (defaults to now)
   * @returns {Object} Duration object with days, hours, minutes, seconds
   */
  static getDuration(startDate, endDate = new Date()) {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    if (!start || !end) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };
    }
    
    const diffMs = Math.abs(end - start);
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds, totalMs: diffMs };
  }

  /**
   * Format duration as a string
   * @param {Object} duration - Duration object from getDuration
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {string} Formatted duration string
   */
  static formatDuration(duration, language = 'bg') {
    const { days, hours, minutes, seconds } = duration;
    
    const parts = [];
    
    const translations = {
      bg: {
        days: (n) => `${n} ${n === 1 ? 'ден' : 'дни'}`,
        hours: (n) => `${n} ${n === 1 ? 'час' : 'часа'}`,
        minutes: (n) => `${n} ${n === 1 ? 'минута' : 'минути'}`,
        seconds: (n) => `${n} ${n === 1 ? 'секунда' : 'секунди'}`
      },
      en: {
        days: (n) => `${n} ${n === 1 ? 'day' : 'days'}`,
        hours: (n) => `${n} ${n === 1 ? 'hour' : 'hours'}`,
        minutes: (n) => `${n} ${n === 1 ? 'minute' : 'minutes'}`,
        seconds: (n) => `${n} ${n === 1 ? 'second' : 'seconds'}`
      }
    };
    
    const t = translations[language];
    
    if (days > 0) parts.push(t.days(days));
    if (hours > 0) parts.push(t.hours(hours));
    if (minutes > 0) parts.push(t.minutes(minutes));
    if (seconds > 0 || parts.length === 0) parts.push(t.seconds(seconds));
    
    return parts.join(' ');
  }

  /**
   * Check if a date is today
   * @param {string|Date} date - Date to check
   * @returns {boolean} True if date is today
   */
  static isToday(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return false;
    
    const today = new Date();
    return parsedDate.getDate() === today.getDate() &&
           parsedDate.getMonth() === today.getMonth() &&
           parsedDate.getFullYear() === today.getFullYear();
  }

  /**
   * Check if a date is tomorrow
   * @param {string|Date} date - Date to check
   * @returns {boolean} True if date is tomorrow
   */
  static isTomorrow(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return false;
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return parsedDate.getDate() === tomorrow.getDate() &&
           parsedDate.getMonth() === tomorrow.getMonth() &&
           parsedDate.getFullYear() === tomorrow.getFullYear();
  }

  /**
   * Check if a date is in the past
   * @param {string|Date} date - Date to check
   * @returns {boolean} True if date is in the past
   */
  static isPast(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return false;
    
    return parsedDate < new Date();
  }

  /**
   * Check if a date is in the future
   * @param {string|Date} date - Date to check
   * @returns {boolean} True if date is in the future
   */
  static isFuture(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return false;
    
    return parsedDate > new Date();
  }

  /**
   * Add days to a date
   * @param {string|Date} date - Base date
   * @param {number} days - Number of days to add (can be negative)
   * @returns {Date|null} New date or null if invalid
   */
  static addDays(date, days) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Add hours to a date
   * @param {string|Date} date - Base date
   * @param {number} hours - Number of hours to add (can be negative)
   * @returns {Date|null} New date or null if invalid
   */
  static addHours(date, hours) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setHours(result.getHours() + hours);
    return result;
  }

  /**
   * Add minutes to a date
   * @param {string|Date} date - Base date
   * @param {number} minutes - Number of minutes to add (can be negative)
   * @returns {Date|null} New date or null if invalid
   */
  static addMinutes(date, minutes) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setMinutes(result.getMinutes() + minutes);
    return result;
  }

  /**
   * Get start of day (00:00:00)
   * @param {string|Date} date - Date
   * @returns {Date|null} Start of day or null if invalid
   */
  static startOfDay(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /**
   * Get end of day (23:59:59)
   * @param {string|Date} date - Date
   * @returns {Date|null} End of day or null if invalid
   */
  static endOfDay(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  /**
   * Get start of week (Monday 00:00:00)
   * @param {string|Date} date - Date
   * @returns {Date|null} Start of week or null if invalid
   */
  static startOfWeek(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    const day = result.getDay();
    const diff = result.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    result.setDate(diff);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /**
   * Get end of week (Sunday 23:59:59)
   * @param {string|Date} date - Date
   * @returns {Date|null} End of week or null if invalid
   */
  static endOfWeek(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    const day = result.getDay();
    const diff = result.getDate() + (day === 0 ? 0 : 7 - day);
    result.setDate(diff);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  /**
   * Get start of month (1st day 00:00:00)
   * @param {string|Date} date - Date
   * @returns {Date|null} Start of month or null if invalid
   */
  static startOfMonth(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setDate(1);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /**
   * Get end of month (last day 23:59:59)
   * @param {string|Date} date - Date
   * @returns {Date|null} End of month or null if invalid
   */
  static endOfMonth(date) {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    const result = new Date(parsedDate);
    result.setMonth(result.getMonth() + 1);
    result.setDate(0);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  /**
   * Get days in a month
   * @param {number} year - Year
   * @param {number} month - Month (0-11)
   * @returns {number} Number of days in month
   */
  static getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  /**
   * Get calendar days for a month (including padding days from previous/next month)
   * @param {number} year - Year
   * @param {number} month - Month (0-11)
   * @returns {Array} Array of date objects for calendar display
   */
  static getCalendarDays(year, month) {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay();
    const startPadding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday as first day
    
    const days = [];
    
    // Previous month padding
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startPadding; i > 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i + 1),
        isCurrentMonth: false,
        isPrevMonth: true,
        isNextMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
        isPrevMonth: false,
        isNextMonth: false
      });
    }
    
    // Next month padding (to complete the week)
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          date: new Date(year, month + 1, i),
          isCurrentMonth: false,
          isPrevMonth: false,
          isNextMonth: true
        });
      }
    }
    
    return days;
  }

  /**
   * Convert timezone
   * @param {string|Date} date - Date to convert
   * @param {string} fromTimezone - Source timezone (e.g., 'UTC', 'Europe/Sofia')
   * @param {string} toTimezone - Target timezone
   * @returns {Date|null} Converted date or null if invalid
   */
  static convertTimezone(date, fromTimezone = 'UTC', toTimezone = 'Europe/Sofia') {
    const parsedDate = this.parseDate(date);
    if (!parsedDate) return null;
    
    try {
      // Use Intl.DateTimeFormat for timezone conversion
      const options = { timeZone: toTimezone };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      
      // Get the offset difference
      const parts = formatter.formatToParts(parsedDate);
      const year = parts.find(p => p.type === 'year').value;
      const month = parts.find(p => p.type === 'month').value;
      const day = parts.find(p => p.type === 'day').value;
      const hour = parts.find(p => p.type === 'hour')?.value || '0';
      const minute = parts.find(p => p.type === 'minute')?.value || '0';
      const second = parts.find(p => p.type === 'second')?.value || '0';
      
      return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    } catch (error) {
      console.error('Timezone conversion error:', error);
      return parsedDate;
    }
  }

  /**
   * Get Bulgarian timezone (Europe/Sofia)
   * @param {string|Date} date - Date to convert
   * @returns {Date|null} Date in Bulgarian timezone
   */
  static toBulgarianTime(date) {
    return this.convertTimezone(date, 'UTC', 'Europe/Sofia');
  }

  /**
   * Compare two dates
   * @param {string|Date} date1 - First date
   * @param {string|Date} date2 - Second date
   * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
   */
  static compareDates(date1, date2) {
    const parsed1 = this.parseDate(date1);
    const parsed2 = this.parseDate(date2);
    
    if (!parsed1 || !parsed2) return 0;
    
    if (parsed1 < parsed2) return -1;
    if (parsed1 > parsed2) return 1;
    return 0;
  }

  /**
   * Check if two dates are the same day
   * @param {string|Date} date1 - First date
   * @param {string|Date} date2 - Second date
   * @returns {boolean} True if same day
   */
  static isSameDay(date1, date2) {
    const parsed1 = this.parseDate(date1);
    const parsed2 = this.parseDate(date2);
    
    if (!parsed1 || !parsed2) return false;
    
    return parsed1.getDate() === parsed2.getDate() &&
           parsed1.getMonth() === parsed2.getMonth() &&
           parsed1.getFullYear() === parsed2.getFullYear();
  }

  /**
   * Get age from birthdate
   * @param {string|Date} birthdate - Birth date
   * @returns {number} Age in years
   */
  static getAge(birthdate) {
    const parsed = this.parseDate(birthdate);
    if (!parsed) return 0;
    
    const today = new Date();
    let age = today.getFullYear() - parsed.getFullYear();
    const monthDiff = today.getMonth() - parsed.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
      age--;
    }
    
    return age;
  }
}

// Export the DateUtils class and constants
// Note: DATE_FORMATS is already exported above (line 16)
export default DateUtils;
export { MONTH_NAMES, DAY_NAMES };

